import { Link, getRouteApi } from '@tanstack/react-router'
import { ArrowLeft, BadgeCheck, Building2, Globe, MapPin, Users } from 'lucide-react'

import { ShareButton } from '@/components/share'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { VacancyRow } from '@/routes/vacancies'
import { useCompanies } from '@/features/company/api'
import { useVacancies } from '@/features/hiring/api'

const route = getRouteApi('/public/perusahaan/$slug')

/**
 * Halaman publik perusahaan.
 *
 * Profil dibaca dari direktori yang sama dengan halaman daftar sehingga satu
 * permintaan sudah cukup; lowongannya disaring dari papan lowongan.
 */
export function CompanyPublicPage() {
  const { slug } = route.useParams()
  const { data, isLoading } = useCompanies()
  const company = data?.companies.find((item) => item.slug === slug)

  const { data: vacancyData } = useVacancies({ q: company?.name })
  const vacancies = (vacancyData?.vacancies ?? []).filter((vacancy) => vacancy.company.slug === slug)

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    )
  }

  if (!company) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Perusahaan tidak ditemukan"
          description="Tautan mungkin sudah berubah."
          action={
            <Link to="/lowongan">
              <Button variant="outline">Lihat lowongan</Button>
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <Link
        to="/lowongan"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Lowongan kerja
      </Link>

      <Card className="mt-4">
        <CardBody className="flex flex-col gap-6 sm:flex-row sm:items-center">
          {company.logo_url ? (
            <img src={company.logo_url} alt="" className="size-20 rounded-2xl border border-line object-cover" />
          ) : (
            <span className="flex size-20 items-center justify-center rounded-2xl bg-brand-soft text-brand">
              <Building2 className="size-9" />
            </span>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">{company.name}</h1>
              {company.verified ? (
                <Badge tone="brand" icon={<BadgeCheck className="size-3.5" />}>
                  Terverifikasi
                </Badge>
              ) : null}
            </div>

            <div className="mt-4">
              <ShareButton title={company.name} text={`${company.name} di skava.id`} label="Bagikan profil" />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
              {company.industry ? <span>{company.industry}</span> : null}
              {company.city ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="size-4" />
                  {company.city}
                </span>
              ) : null}
              {company.size ? (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="size-4" />
                  {company.size} karyawan
                </span>
              ) : null}
              {company.website ? (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 font-semibold text-brand hover:underline"
                >
                  <Globe className="size-4" />
                  Situs web
                </a>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>

      {company.about ? (
        <Card className="mt-6">
          <CardHeader title="Tentang perusahaan" />
          <CardBody>
            <p className="whitespace-pre-line leading-relaxed text-muted">{company.about}</p>
          </CardBody>
        </Card>
      ) : null}

      <section className="mt-8">
        <h2 className="text-xl font-extrabold tracking-tight text-ink">
          Lowongan terbuka ({vacancies.length})
        </h2>

        {vacancies.length === 0 ? (
          <Card className="mt-4">
            <EmptyState
              title="Belum ada lowongan terbuka"
              description="Simpan halaman ini; lowongan baru akan muncul di sini."
            />
          </Card>
        ) : (
          <div className="mt-4 space-y-3">
            {vacancies.map((vacancy) => (
              <VacancyRow key={vacancy.id} vacancy={vacancy} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
